import axios from "axios";
import { BASE_URL } from "../config/constants";

// Create a separate axios instance for auth operations to avoid circular imports
const authApi = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

interface AuthPayload {
  userId: string;
  accessToken: string;
  refreshToken: string;
  role: string;
  accessTokenExpiresAt: number;
  refreshTokenExpiresAt: number;
}

// Store user authentication data with both access and refresh tokens
export const storeAuthData = (
  userId: string,
  accessToken: string,
  refreshToken: string,
  role: string
) => {
  const accessTokenExpiresAt = Date.now() + 30 * 60 * 1000; // 30 minutes from now
  const refreshTokenExpiresAt = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days from now

  const authData: AuthPayload = {
    userId,
    accessToken,
    refreshToken,
    role,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
  };

  // Clear any existing auth data first
  clearAuthData();

  // Store new auth data
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("userId", userId);
  localStorage.setItem("role", role);
  localStorage.setItem("accessTokenExpiresAt", accessTokenExpiresAt.toString());
  localStorage.setItem(
    "refreshTokenExpiresAt",
    refreshTokenExpiresAt.toString()
  );
  localStorage.setItem("isAuthenticated", "true");

  // Verify storage was successful
  const storedAccessToken = localStorage.getItem("accessToken");
  const storedRefreshToken = localStorage.getItem("refreshToken");
  const storedUserId = localStorage.getItem("userId");

  if (
    storedAccessToken !== accessToken ||
    storedRefreshToken !== refreshToken ||
    storedUserId !== userId
  ) {
    if (process.env.NODE_ENV === "development") {
      console.error(
        "SuperadminFE: Auth data storage failed - values don't match"
      );
    }
    return null;
  }

  if (process.env.NODE_ENV === "development") {
    console.log("SuperadminFE: Auth data stored successfully", {
      userId,
      role,
      accessTokenLength: accessToken.length,
      refreshTokenLength: refreshToken.length,
      accessTokenExpiresAt: new Date(accessTokenExpiresAt).toLocaleString(),
      refreshTokenExpiresAt: new Date(refreshTokenExpiresAt).toLocaleString(),
    });
  }

  return authData;
};

// Update only the access token (used after refresh)
export const updateAccessToken = (newAccessToken: string) => {
  const newAccessTokenExpiresAt = Date.now() + 30 * 60 * 1000; // 30 minutes from now

  localStorage.setItem("accessToken", newAccessToken);
  localStorage.setItem(
    "accessTokenExpiresAt",
    newAccessTokenExpiresAt.toString()
  );

  if (process.env.NODE_ENV === "development") {
    console.log("SuperadminFE: Access token updated successfully", {
      accessTokenLength: newAccessToken.length,
      expiresAt: new Date(newAccessTokenExpiresAt).toISOString(),
    });
  }
};

// Validate that authentication data is properly stored
export const validateAuthStorage = (): boolean => {
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");
  const userId = localStorage.getItem("userId");
  const role = localStorage.getItem("role");
  const accessTokenExpiresAtStr = localStorage.getItem("accessTokenExpiresAt");
  const refreshTokenExpiresAtStr = localStorage.getItem(
    "refreshTokenExpiresAt"
  );
  const isAuthStr = localStorage.getItem("isAuthenticated");

  return !!(
    accessToken &&
    refreshToken &&
    userId &&
    role &&
    accessTokenExpiresAtStr &&
    refreshTokenExpiresAtStr &&
    isAuthStr === "true"
  );
};

// Retrieve and validate authentication data
export const getAuthData = (): AuthPayload | null => {
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");
  const userId = localStorage.getItem("userId");
  const role = localStorage.getItem("role");
  const accessTokenExpiresAtStr = localStorage.getItem("accessTokenExpiresAt");
  const refreshTokenExpiresAtStr = localStorage.getItem(
    "refreshTokenExpiresAt"
  );

  if (
    !accessToken ||
    !refreshToken ||
    !userId ||
    !role ||
    !accessTokenExpiresAtStr ||
    !refreshTokenExpiresAtStr
  ) {
    return null;
  }

  const accessTokenExpiresAt = parseInt(accessTokenExpiresAtStr, 10);
  const refreshTokenExpiresAt = parseInt(refreshTokenExpiresAtStr, 10);

  // Check if refresh token has expired
  if (Date.now() > refreshTokenExpiresAt) {
    if (process.env.NODE_ENV === "development") {
      console.log("SuperadminFE: Refresh token expired, clearing auth data");
    }
    clearAuthData();
    return null;
  }

  return {
    accessToken,
    refreshToken,
    userId,
    role,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
  };
};

// Get access token
export const getAccessToken = (): string | null => {
  const authData = getAuthData();
  return authData?.accessToken || null;
};

// Get refresh token
export const getRefreshToken = (): string | null => {
  const authData = getAuthData();
  return authData?.refreshToken || null;
};

// Check if user is authenticated (has valid refresh token)
export const isAuthenticated = (): boolean => {
  const authData = getAuthData();
  return authData !== null;
};

// Check if access token is expired
export const isAccessTokenExpired = (): boolean => {
  const authData = getAuthData();
  if (!authData) return true;

  return Date.now() > authData.accessTokenExpiresAt;
};

// Check if session is about to expire (2 minutes before expiration)
export const isSessionAboutToExpire = (): boolean => {
  const authData = getAuthData();
  if (!authData) return false;

  const twoMinutesFromNow = Date.now() + 2 * 60 * 1000; // 2 minutes from now
  return twoMinutesFromNow > authData.accessTokenExpiresAt;
};

// Get time until access token expires (in milliseconds)
export const getTimeUntilExpiry = (): number => {
  const authData = getAuthData();
  if (!authData) return 0;

  const timeUntilExpiry = authData.accessTokenExpiresAt - Date.now();
  return Math.max(0, timeUntilExpiry);
};

// Clear all authentication data
export const clearAuthData = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("role");
  localStorage.removeItem("accessTokenExpiresAt");
  localStorage.removeItem("refreshTokenExpiresAt");
  localStorage.removeItem("isAuthenticated");

  // Also clear legacy token storage for backward compatibility
  localStorage.removeItem("token");
  localStorage.removeItem("expiresAt");

  if (process.env.NODE_ENV === "development") {
    console.log("SuperadminFE: Auth data cleared");
  }
};

// Get the stored token (for backward compatibility)
export const getToken = (): string | null => {
  return getAccessToken();
};

// Refresh the access token using the refresh token
export const refreshAccessToken = async (): Promise<boolean> => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      if (process.env.NODE_ENV === "development") {
        console.error("SuperadminFE: No refresh token available");
      }
      return false;
    }

    if (process.env.NODE_ENV === "development") {
      console.log("SuperadminFE: Attempting to refresh access token");
    }

    // Call the refresh endpoint with refresh token in Authorization header
    const response = await authApi.post(
      "/login/refresh",
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );

    // Extract new tokens from response
    const {
      accessToken,
      refreshToken: newRefreshToken,
      userId,
      role,
    } = response.data;

    if (!accessToken || !newRefreshToken || !userId || !role) {
      if (process.env.NODE_ENV === "development") {
        console.error("SuperadminFE: Invalid response from refresh endpoint");
      }
      return false;
    }

    // Store the new tokens
    const newAuthData = storeAuthData(
      userId,
      accessToken,
      newRefreshToken,
      role
    );

    if (process.env.NODE_ENV === "development") {
      console.log("SuperadminFE: Access token refreshed successfully", {
        newAccessTokenLength: accessToken.length,
        newRefreshTokenLength: newRefreshToken.length,
        newExpiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
      });
    }
    return !!newAuthData;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("SuperadminFE: Failed to refresh access token:", error);
    }

    // If refresh fails, clear auth data and redirect to login
    clearAuthData();

    // Only redirect if we're not already on the login page
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }

    return false;
  }
};

// Legacy function names for backward compatibility
export const refreshToken = async (): Promise<boolean> => {
  return await refreshAccessToken();
};

export const extendSession = async (): Promise<boolean> => {
  return await refreshAccessToken();
};

// Check if token is about to expire (within 5 minutes) - legacy function
export const isTokenExpiringSoon = (): boolean => {
  return isSessionAboutToExpire();
};

// Force logout and redirect to login
export const forceLogout = async () => {
  if (process.env.NODE_ENV === "development") {
    console.log("SuperadminFE: Forcing logout due to token expiry");
  }

  // Try to call logout API endpoint, but don't let it block the logout process
  try {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await authApi.post("/login/logout", { refreshToken });
      if (process.env.NODE_ENV === "development") {
        console.log(
          "SuperadminFE: Logout API call successful during forceLogout"
        );
      }
    } else {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          "SuperadminFE: No refresh token available for logout API call"
        );
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error(
        "SuperadminFE: Logout API call failed during forceLogout:",
        error
      );
    }
    // Continue with logout even if API call fails
  }

  clearAuthData();
  // Redirect to login if we're not already there
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// Migrate legacy auth data that doesn't have dual tokens
export const migrateLegacyAuthData = (): boolean => {
  const legacyToken = localStorage.getItem("token");
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");

  // If we have legacy token but no dual tokens, we need to clear and force re-login
  if (legacyToken && !accessToken && !refreshToken) {
    if (process.env.NODE_ENV === "development") {
      console.log(
        "SuperadminFE: Legacy token detected, clearing for dual-token migration"
      );
    }
    clearAuthData();
    return false;
  }

  return true;
};

// Development utility function to test session expiration
export const simulateTokenExpiry = (minutesFromNow: number = 0): void => {
  if (process.env.NODE_ENV !== "development") {
    console.warn(
      "SuperadminFE: simulateTokenExpiry can only be used in development mode"
    );
    return;
  }

  const newExpiryTime = Date.now() + minutesFromNow * 60 * 1000;
  localStorage.setItem("accessTokenExpiresAt", newExpiryTime.toString());

  console.log(
    `SuperadminFE: Token expiry simulated: ${minutesFromNow} minutes from now`,
    {
      expiresAt: new Date(newExpiryTime).toISOString(),
      isExpired: isAccessTokenExpired(),
      isAboutToExpire: isSessionAboutToExpire(),
    }
  );
};

// Add to window for easy testing in development
if (process.env.NODE_ENV === "development") {
  (window as any).testSuperadminSessionExpiry = {
    simulateExpiry: simulateTokenExpiry,
    showWarning: () => simulateTokenExpiry(1.5), // Show warning in 1.5 minutes
    expireNow: () => simulateTokenExpiry(-1), // Expire immediately
    checkStatus: () => ({
      isAuthenticated: isAuthenticated(),
      isExpired: isAccessTokenExpired(),
      isAboutToExpire: isSessionAboutToExpire(),
      timeUntilExpiry: getTimeUntilExpiry(),
    }),
  };
}
